import { h, FunctionalComponent } from "preact";
import styled from "styled-components";
import { DelYeahContainer } from "../../../containers/DelYeahContainer";
import { Controller, useForm } from "react-hook-form";
import { link, Typography, objectIsEmpty } from '@authentic/loyalty-pwa/util';
import { TextField } from "@authentic/loyalty-pwa/components/Form";
import {
  backgroundColors,
  breakpoints,
} from "@authentic/loyalty-pwa/util/variables";
import { Button } from "@authentic/loyalty-pwa/components";
import { ErrorMessage } from "../../../components/Error";
import { useContext, useState } from "preact/hooks";
import { CheckIcon } from "../../../icons/Check";
import { CloseIcon } from "../../../icons/Close";
import { OfferServiceContext } from "@authentic/loyalty-pwa/providers";

const FormStyled = styled.form`
  background: #ffffff;
  text-align: left;
`;

const CouponStyled = styled.div`
  background: ${backgroundColors.white};
  padding: 50px 30px;

  @media screen and (min-width: ${breakpoints.sm}) {
    background: ${backgroundColors.lightGray};
  }
`;

const Headline = styled.div`
  margin-bottom: 16px;
  text-align: center;
`;

const FormControlStyled = styled.div`
  margin-bottom: 12px;
`;

const WhiteBox = styled.div`
  padding: 0 0 30px;
  text-align: center;

  @media screen and (min-width: ${breakpoints.sm}) {
    background: ${backgroundColors.white};
    margin: 0 auto;
    max-width: 550px;
    padding: 40px 50px;
  }
`;

const CloseMessageButton = styled.button`
  background-color: transparent;
  border: none;
  cursor: pointer;
  overflow: hidden;
  outline: none;

  @media screen and (max-width: ${breakpoints.md}) {
    position: absolute;
    top: 3px;
    right: 3px;
  }
`;

const MessageStyle = styled.div`
  background-color: #40ad5f;
  height: 55px;
  width: 100%;
  margin-top: 1px;
  font-family: Nunito Sans;
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
  line-height: 22px;
  color: white;
  padding-top: 17px;
  text-align: center;

  @media screen and (max-width: ${breakpoints.md}) {
    width: 95%;
    position: absolute;
    bottom: 10px;
    left: 2.5%;
    border-radius: 5px;
    padding-left: 20px;
    padding-top: 21px;
    height: 90px;
    box-shadow: 2px 2px 5px #999;
    text-align: left;

    span {
      display: block;
    }
  }
`;

export const DelYeahEnterCoupon: FunctionalComponent = () => {
  const htmlTitle: string = "Enter Coupon";
  const htmlMetaDescription: string =
    "Your Del Yeah! Rewards Dashboard of available rewards and offers.";
  const IDItemMenuSelected: string = "4";
  const StringToSearchInErrorMessage = 'registration';
  const StringToReplaceInErrorMessage = 'coupon';
  const [errorMessage, setErrorMessage] = useState("");
  const [showMessage, setShowMessage] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const offerService = useContext(OfferServiceContext);

  const {
    handleSubmit,
    control,
    formState: { errors, isDirty },
    setError,
    setValue,
  } = useForm();

  const onSubmit = (data: any) => {
    redeemOfferCode(data.coupon);
  };

  const closeMessage = (e: any) => {
    setShowMessage(false);
  };

  const firstLetter = /(?!.*[DFIOQU])[A-VXY]/i;
  const letter = /(?!.*[DFIOQU])[A-Z]/i;
  const digit = /[0-9]/;
  const mask = [firstLetter, digit, letter, " ", digit, letter, digit];

  const redeemOfferCode = (code: string) => {
    offerService?.redeemOfferCode(code).then((res) => {
      if (res.success) {
        setShowMessage(true);
        setValue("coupon", "");
      } else {
        // set custom error message regarding to DTLA-162: replacing the registration word by coupon word
        const errorMessage = res.message.toLowerCase();
        const indexStringSearched = errorMessage.indexOf(StringToSearchInErrorMessage);
        if (indexStringSearched > -1) {
          if (indexStringSearched === 0) {
            setError("coupon", {
              message: res.message.replace(
                `${StringToSearchInErrorMessage.charAt(0).toUpperCase()}${StringToSearchInErrorMessage.slice(1)}`,
                `${StringToReplaceInErrorMessage.charAt(0).toUpperCase()}${StringToReplaceInErrorMessage.slice(1)}`,
              ) 
            });
          } else {
            setError("coupon", {
              message: res.message.replace(StringToSearchInErrorMessage, StringToReplaceInErrorMessage) 
            });
          }
        } else {
          setError("coupon", { message: res.message });
        }
      }
    });
  };

  return (
    <DelYeahContainer
      htmlTitle={htmlTitle}
      htmlMetaDescription={htmlMetaDescription}
      IDItemMenuSelected={IDItemMenuSelected}
    >
      {showMessage && (
        <MessageStyle>
          <CheckIcon /> <b>Coupon applied!</b>{" "}
          <span>Head to the <a
            href={link('/scan-code')}
            style={{ textDecoration: 'underline' }}>Scan</a> page to use it.</span>
          <CloseMessageButton onClick={closeMessage}>
            <CloseIcon />
          </CloseMessageButton>
        </MessageStyle>
      )}

      <CouponStyled>
        <Headline>
          <Typography variant="headline1">COUPON CODE</Typography>
        </Headline>
        <WhiteBox className="white-box">
          <FormStyled onSubmit={handleSubmit(onSubmit)}>
            <FormControlStyled>
              <Controller
                render={({ field }: any): any => (
                  <TextField
                    error={errors?.coupon}
                    label="Coupon Code"
                    autoFocus
                    type="text"
                    required
                    {...field}
                    id="coupon"
                  />
                )}
                rules={{
                  required: {
                    value: true,
                    message: "Please enter your coupon",
                  },
                }}
                control={control}
                name="coupon"
                defaultValue=""
              />
            </FormControlStyled>
            {(!objectIsEmpty(errors) || errorMessage) && (
              <ErrorMessage>
                {errorMessage
                  ? errorMessage
                  : "Review highlighted errors to continue"}
              </ErrorMessage>
            )}
            <Button
              ariaLabel="NEXT"
              type="submit"
              disabled={!isDirty || isLoading}
              fullWidth
            >
              APPLY
            </Button>
          </FormStyled>
        </WhiteBox>
      </CouponStyled>
    </DelYeahContainer>
  );
};
